import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CHECKOUT_URL, galleryImages } from '../data/salesContent';
import { trackInitiateCheckout } from '../utils/metaPixel';
const AUTOPLAY_MS = 4500;
export function GalleryCarousel() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [paused, setPaused] = useState(false);
  const dragged = useRef(false);
  const reduceMotion = useReducedMotion();
  const count = galleryImages.length;
  const go = useCallback((next: number, dir: number) => {
    setState([(next + count) % count, dir]);
  }, [count]);
  useEffect(() => {
    if (paused || reduceMotion) return;
    const id = setTimeout(() => go(index + 1, 1), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [index, paused, reduceMotion, go]);
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 60) {
      dragged.current = true;
      go(index + (info.offset.x < 0 ? 1 : -1), info.offset.x < 0 ? 1 : -1);
    }
  };
  const current = galleryImages[index];
  return <div className="mx-auto max-w-lg" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} role="region" aria-roledescription="carrusel" aria-label="Imágenes del MegaPack" data-id="element-28" data-mp="3bil2oqr2d126">
      <div className="relative" data-id="element-29" data-mp="83fc0u9860dr">
        <div className="relative mx-auto aspect-[537/680] w-full overflow-hidden rounded-2xl border border-line bg-white shadow-2xl shadow-black/40" data-id="element-30" data-mp="d0pb71mf1qes">
          <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-31" data-mp="3uekxsafc8kpz">
            <motion.a key={index} href={CHECKOUT_URL} onClick={e => {
            if (dragged.current) {
              e.preventDefault();
              dragged.current = false;
              return;
            }
            trackInitiateCheckout();
          }} custom={direction} initial={{
            opacity: 0,
            x: direction * 40
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -40
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.2} onDragEnd={onDragEnd} className="absolute inset-0 block cursor-grab focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-accent/50 active:cursor-grabbing" aria-label={`${current.alt} — comprar el MegaPack`} data-id="element-32" data-mp="38rujfcn268o">
              <img src={current.src} alt={current.alt} draggable={false} className="h-full w-full select-none object-contain" data-id="element-33" data-mp="cvm5vlei27xp" />
            </motion.a>
          </AnimatePresence>
          <p className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-canvas/80 px-2.5 py-1 text-xs font-semibold tabular-nums text-ink" data-id="element-34" data-mp="3wigjzoqowar">
            {index + 1} / {count}
          </p>
        </div>
        <button type="button" onClick={() => go(index - 1, -1)} aria-label="Imagen anterior" className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-canvas/90 text-ink transition-colors duration-150 hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 md:-left-5" data-id="element-35" data-mp="2anwxp2lmg03m">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-36" data-mp="1rsyh5xdknl2a" />
        </button>
        <button type="button" onClick={() => go(index + 1, 1)} aria-label="Imagen siguiente" className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-canvas/90 text-ink transition-colors duration-150 hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 md:-right-5" data-id="element-37" data-mp="1wue11m40jlj3">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-38" data-mp="1d4lphclioodl" />
        </button>
      </div>

      <ul className="mt-5 grid grid-cols-4 gap-2 md:gap-3" data-id="element-39" data-mp="u21pnrcpaik3">
        {galleryImages.map((img, i) => <li key={img.src} data-id="element-40" data-mp="p3ryrsy0e44a" data-mpi={i}>
            <button type="button" onClick={() => go(i, i > index ? 1 : -1)} aria-label={`Ver imagen ${i + 1}`} aria-current={i === index} className={`block aspect-[4/5] w-full overflow-hidden rounded-lg border-2 bg-white transition-[border-color,opacity] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${i === index ? 'border-accent opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`} data-id="element-41" data-mp="p0m663q68k3t">
              <img src={img.src} alt="" className="h-full w-full object-cover object-top" data-id="element-42" data-mp="1mqqthy5551zj" />
            </button>
          </li>)}
      </ul>
    </div>;
}